import { useEffect, useRef, useState } from 'react'

/**
 * A few seconds between "Send" and sending: the action waits out `seconds`
 * (counting down, so the screen can say so), and Undo stops it with nothing
 * sent. If the screen goes away first, the action runs then — it was
 * confirmed, and leaving the page must not quietly cancel it.
 */
export function useGraceAction(seconds = 10) {
  const [left, setLeft] = useState<number | null>(null)
  const pending = useRef<{ run: () => void; timer: ReturnType<typeof setInterval> } | null>(null)

  const fire = () => {
    const current = pending.current
    if (!current) return
    clearInterval(current.timer)
    pending.current = null
    setLeft(null)
    current.run()
  }

  // Leaving the screen, or closing the page, sends it now.
  useEffect(() => {
    const leaving = () => fire()
    globalThis.addEventListener?.('pagehide', leaving)
    return () => {
      globalThis.removeEventListener?.('pagehide', leaving)
      fire()
    }
  }, [])

  return {
    /** Seconds until it runs, or null when nothing is waiting. */
    left,
    start(run: () => void) {
      if (pending.current) return
      let remaining = seconds
      setLeft(remaining)
      const timer = setInterval(() => {
        remaining -= 1
        if (remaining <= 0) fire()
        else setLeft(remaining)
      }, 1000)
      pending.current = { run, timer }
    },
    /** Stops it: nothing happens. Returns whether there was anything to stop. */
    undo(): boolean {
      const current = pending.current
      if (!current) return false
      clearInterval(current.timer)
      pending.current = null
      setLeft(null)
      return true
    },
    /** Runs it now, without waiting out the rest. */
    now: fire,
  }
}
